import React, { useState } from 'react';
import { Heart, ShoppingCart, Plus, Star, CheckCircle } from 'lucide-react';
import ProductSaveButtons from './ProductSaveButtons';

const ProductRecommendations = ({ recommendations, onSave, customListNames, createCustomList = null }) => {
  const [savedProducts, setSavedProducts] = useState(new Set());

  // If invalid recommendations format, don't render anything
  if (!recommendations || !recommendations.product_matches) {
    return null;
  }

  const handleSave = (product, category) => {
    onSave(product, category);
    setSavedProducts(prev => new Set([...prev, product.id]));
  };

  const renderProductCard = (product, index, category) => {
    const productId = `${category}-${index}`;
    const isSaved = savedProducts.has(productId);
    
    return (
      <div key={productId} className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h4 className="font-medium text-[#2A324B] text-sm mb-1">
              {product.brand} - {product.product}
            </h4>
            {product.recommended_shades && (
              <p className="text-xs text-[#2A324B]/70 mb-2">
                <span className="font-medium">Shades:</span> {product.recommended_shades.join(', ')}
              </p>
            )}
            {product.price_range && (
              <p className="text-xs text-[#2A324B]/70 mb-2">
                <span className="font-medium">Price:</span> {product.price_range}
              </p>
            )}
            {product.match_reasoning && (
              <p className="text-xs text-[#2A324B]/80 mb-2">
                <span className="font-medium">Why it matches:</span> {product.match_reasoning}
              </p>
            )}
            {product.finish && (
              <p className="text-xs text-[#2A324B]/70 mb-2">
                <span className="font-medium">Finish:</span> {product.finish}
              </p>
            )}
          </div>
          {isSaved && (
            <div className="flex items-center gap-1 text-green-600 text-xs">
              <CheckCircle className="h-4 w-4" />
              <span>Saved</span>
            </div>
          )}
        </div>
        
        {!isSaved && (
          <ProductSaveButtons
            productImage=""
            productName={product.product}
            productBrand={product.brand}
            onSave={(productData, category) => handleSave({ ...productData, id: productId }, category)}
            customListNames={customListNames}
            createCustomList={createCustomList}
          />
        )}
      </div>
    );
  };

  const renderProductSection = (title, icon, products, category) => {
    if (!products || products.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          {icon}
          <h3 className="text-sm font-medium text-[#2A324B]">{title}</h3>
        </div>
        <div className="space-y-3">
          {products.map((product, index) => renderProductCard(product, index, category))}
        </div>
      </div>
    );
  };

  const matches = recommendations.product_matches;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Star className="h-5 w-5 text-yellow-500" />
        <h2 className="text-base font-medium text-[#2A324B]">
          Product Recommendations Based on Your Skin Analysis
        </h2>
      </div>

      {renderProductSection(
        "Foundation Matches",
        <span className="text-lg">💄</span>,
        matches.foundation_matches,
        "foundation"
      )}

      {renderProductSection(
        "Lipstick Matches", 
        <span className="text-lg">💋</span>,
        matches.lipstick_matches,
        "lipstick"
      )}

      {renderProductSection(
        "Blush Matches",
        <span className="text-lg">🌸</span>,
        matches.blush_matches,
        "blush"
      )}

      {renderProductSection(
        "Eyeshadow Matches",
        <span className="text-lg">👁️</span>,
        matches.eyeshadow_matches,
        "eyeshadow"
      )}

      {matches.shopping_strategy && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-medium text-blue-800 mb-2">🛍️ Shopping Tips</h4>
          {matches.shopping_strategy.priority_order && (
            <p className="text-xs text-blue-700 mb-1">
              <span className="font-medium">Priority:</span> {matches.shopping_strategy.priority_order.join(' → ')}
            </p>
          )}
          {matches.shopping_strategy.color_coordination && (
            <p className="text-xs text-blue-700">
              <span className="font-medium">Color coordination:</span> {matches.shopping_strategy.color_coordination}
            </p>
          )}
        </div>
      )}

      <div className="mt-4 text-xs text-[#2A324B]/70 text-center">
        Want recommendations for other products? Just ask for "lipstick recommendations" or "blush suggestions"!
      </div>

      <div className="mt-3 pt-3 border-t border-[#341C1C]/8 flex items-start gap-1.5 text-[13px] text-[#2A324B]/50 leading-snug">
        <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
        <span>These picks are based only on your skin — none are paid promotions and we earn no commission from recommending them.</span>
      </div>

    </div>
  );
};

export default ProductRecommendations;


